// Moteur de calcul paie marocaine — net cible → brut.
// Paramètres et barème éditables : tout changement de taux est automatiquement pris en compte.

// Indemnités à plafond fixe (Type A — pas de justificatif requis), dans l'ordre de remplissage du gap.
export const INDEMNITE_ORDER = ['transport', 'salissure', 'outillage', 'panier', 'caisse', 'lait', 'tournee'];

export const DEFAULT_PARAMS = {
  cnss_taux: 0.0448,
  cnss_plafond_brut: 6000,
  cnss_montant_max: 268.80,
  amo_taux: 0.0226,
  fraispro_taux:     0.35,      // tranche 1 : SBI mensuel ≤ fraispro_seuil (78 000 / 12)
  fraispro_plafond:  2500,      // plafond mensuel tranche 1 (30 000 / 12)
  fraispro_seuil:    6500,      // seuil mensuel de bascule (78 000 / 12)
  fraispro_taux2:    0.25,      // tranche 2 : SBI mensuel > fraispro_seuil
  fraispro_plafond2: 2916.67,   // plafond mensuel tranche 2 (35 000 / 12)
  smig_brut: 3422.72,
  net_smig: 3192.03,
  // Indemnités à plafond fixe — chacune activable indépendamment
  indemnites: {
    transport: { label: 'Transport domicile-travail', plafond: 500, actif: true },
    salissure: { label: 'Salissure / Bleu de travail', plafond: 210, actif: true },
    outillage: { label: 'Outillage', plafond: 100, actif: true },
    panier:    { label: 'Panier / Casse-croûte', plafond: 880, actif: true },
    caisse:    { label: 'Caisse (agents manipulant des fonds)', plafond: 190, actif: false },
    lait:      { label: 'Lait (produits toxiques)', plafond: 150, actif: false },
    tournee:   { label: 'Prime de tournée (personnel itinérant)', plafond: 1500, actif: false },
  },
  // Indemnité de représentation = % du brut — cadres dirigeants, activable par employé
  representation: { taux: 0.10, actif: false },
  // Réduction IR — charges de famille (LF 2026) : montant/mois/personne, plafonné à N personnes
  charge_famille_montant: 50,
  charge_famille_max: 6,
  // CIMR — retraite complémentaire facultative : cotisation salarié déductible IR, % du brut plafonnable
  cimr: { taux: 0, plafond: Infinity, actif: false },
  // Mutuelle patronale = % du brut, charge employeur hors bulletin (informatif, "coût employeur")
  mutuelle_patronale: { taux: 0, actif: false },
  // Charges patronales statutaires — % du brut (AP), pour un "coût employeur" réaliste
  charges_patronales: {
    cnss_patronale:            { taux: 0.0898, actif: true }, // plafonné comme la CNSS salariale (cnss_plafond_brut)
    allocations_familiales:    { taux: 0.064,  actif: true }, // non plafonné
    formation_professionnelle: { taux: 0.016,  actif: true }, // non plafonné
    amo_patronale:             { taux: 0.0411, actif: true }, // non plafonné
  },
};

// [seuil_haut, taux, déduction] — base imposable AX < seuil_haut
export const DEFAULT_BAREME_IR = [
  [3333.34,  0.00,    0      ],
  [5000.01,  0.10,    333.33 ],
  [6666.68,  0.20,    833.33 ],
  [8333.34,  0.30,    1500   ],
  [15000.01, 0.34,    1833.33],
  [Infinity, 0.37,    2283.33],
];

export function baremeIR(AX, bareme = DEFAULT_BAREME_IR) {
  for (const [seuil, taux, ded] of bareme) {
    if (AX < seuil) return Math.max(0, AX * taux - ded);
  }
  return 0;
}

// Sens direct : brut → net.
// autresDeductions = prêt/avances salarié : réduisent la base IR ET le net BF (déductibles IR).
// mutuelleSalarie = cotisation mutuelle salarié : réduit le net BF mais reste dans la base IR (non déductible).
// representationActif : ajoute representation.taux × AP au net (indemnité de représentation, cadres dirigeants).
// cimrActif : cotisation CIMR (% du brut, plafonnable) — réduit AX et BF (déductible IR).
// avantagesNature : montant non-cash imposable — augmente AX (donc l'IR) sans augmenter BF.
export function netFromBrut(brut, anciennete, indemnitesTotal, autresDeductions, params = DEFAULT_PARAMS, bareme = DEFAULT_BAREME_IR, personnesACharge = 0, representationActif = false, mutuelleSalarie = 0, cimrActif = false, avantagesNature = 0) {
  const AP = brut * anciennete;
  const cnss = AP < params.cnss_plafond_brut ? AP * params.cnss_taux : params.cnss_montant_max;
  const amo = AP * params.amo_taux;
  const fraisPro = AP <= (params.fraispro_seuil ?? Infinity)
    ? Math.min(AP * params.fraispro_taux, params.fraispro_plafond)
    : Math.min(AP * (params.fraispro_taux2 ?? 0.25), params.fraispro_plafond2 ?? 2916.67);
  // CIMR — cotisation salarié facultative, déductible de l'IR, plafonnable
  const cimr = (cimrActif && params.cimr?.actif) ? Math.min(AP * params.cimr.taux, params.cimr.plafond) : 0;
  // Prêt/avances et CIMR réduisent AX (base IR) — les avantages en nature l'augmentent (imposables)
  // (la mutuelle salarié N'EST PAS soustraite ici : elle reste imposable)
  const AX = AP - cnss - amo - autresDeductions - cimr - fraisPro + avantagesNature;
  // Charges de famille (LF 2026) : réduction de l'IR, plafonnée à charge_famille_max personnes
  const chargeFamille = Math.min(Number(personnesACharge) || 0, params.charge_famille_max) * params.charge_famille_montant;
  const ir = Math.max(0, baremeIR(AX, bareme) - chargeFamille);
  // Frais pro ne se soustraient PAS du net BF (entrent uniquement dans AX) ; avantages en nature non plus (non-cash)
  const BF = AP - cnss - amo - autresDeductions - cimr - ir;
  const representation = (representationActif && params.representation?.actif) ? AP * params.representation.taux : 0;
  // Mutuelle salarié : retenue sur le net, non déductible de l'IR
  const netFinal = Math.ceil(BF + indemnitesTotal + representation - mutuelleSalarie);
  // Coût employeur (informatif) : brut + charges patronales statutaires + mutuelle patronale, hors bulletin du salarié
  const cp = params.charges_patronales || {};
  const cnssPatronale = cp.cnss_patronale?.actif ? Math.min(AP, params.cnss_plafond_brut) * cp.cnss_patronale.taux : 0;
  const allocationsFamiliales = cp.allocations_familiales?.actif ? AP * cp.allocations_familiales.taux : 0;
  const formationProfessionnelle = cp.formation_professionnelle?.actif ? AP * cp.formation_professionnelle.taux : 0;
  const amoPatronale = cp.amo_patronale?.actif ? AP * cp.amo_patronale.taux : 0;
  const mutuellePatronale = params.mutuelle_patronale?.actif ? AP * params.mutuelle_patronale.taux : 0;
  const chargesPatronales = cnssPatronale + allocationsFamiliales + formationProfessionnelle + amoPatronale + mutuellePatronale;
  const coutEmployeur = AP + chargesPatronales;
  return { AP, cnss, amo, fraisPro, cimr, AX, ir, BF, representation, mutuellePatronale, chargesPatronales, coutEmployeur, netFinal };
}

// Indemnités à plafond fixe — logique partielle identique au VBA (macro CommandButton1) :
// on remplit les indemnités actives dans l'ordre INDEMNITE_ORDER jusqu'au gap net_cible − SMIG net,
// chacune reçoit le reliquat dans la limite de son plafond, quel que soit le montant restant
// (alignement RH/Cegid : aucun seuil minimum ne bascule le reliquat en prime taxable).
// joursTravailles (sur 26 = mois complet) proratise à la fois le SMIG net de référence et les
// plafonds des indemnités — un journalier à temps partiel ne peut être comparé au SMIG net plein mois.
export function calculerIndemnites(statut, net_cible, params = DEFAULT_PARAMS, joursTravailles = 26) {
  const zero = INDEMNITE_ORDER.reduce((acc, k) => { acc[k] = 0; return acc; }, { total: 0 });
  if (statut === 'M') return zero;

  // Plafonds (et SMIG net de référence) proratisés selon les jours travaillés (26 = mois complet)
  const prorata = Math.min(Number(joursTravailles) || 26, 26) / 26;

  const gap = net_cible - params.net_smig * prorata;
  if (gap <= 0) return zero;

  let remaining = gap;
  const result = { total: 0 };

  for (const key of INDEMNITE_ORDER) {
    const conf = params.indemnites[key];
    if (!conf || !conf.actif) { result[key] = 0; continue; }

    const plafond = conf.plafond * prorata;
    const val = Math.min(remaining, plafond);
    result[key] = val;
    remaining -= val;
    result.total += val;
  }

  return result;
}

// Inversion : net cible → brut. Dichotomie sur netFromBrut (strictement croissante).
export function brutFromNet(net_cible, anciennete, statut, autresDeductions, params = DEFAULT_PARAMS, bareme = DEFAULT_BAREME_IR, joursTravailles = 26, personnesACharge = 0, representationActif = false, mutuelleSalarie = 0, cimrActif = false, avantagesNature = 0) {
  const indDetail = calculerIndemnites(statut, net_cible, params, joursTravailles);
  // bfCible = BF(brut) + representation(brut), les deux croissants avec brut → dichotomie valide
  // (mutuelleSalarie est une retenue fixe, indépendante du brut → reportée dans la cible)
  const bfCible = net_cible - indDetail.total + mutuelleSalarie;

  let lo = 0;
  let hi = 1_000_000;
  for (let k = 0; k < 100; k++) {
    const mid = (lo + hi) / 2;
    const { BF, representation } = netFromBrut(mid, anciennete, 0, autresDeductions, params, bareme, personnesACharge, representationActif, 0, cimrActif, avantagesNature);
    if (BF + representation < bfCible) lo = mid; else hi = mid;
  }
  return { brut: (lo + hi) / 2, indDetail };
}

// Calcule une ligne employé complète + validation (réinjection brut → net).
export function computeEmploye(emp, params = DEFAULT_PARAMS, bareme = DEFAULT_BAREME_IR) {
  const anciennete = Number(emp.anciennete) || 1;
  const net_cible = Number(emp.net_cible) || 0;
  const autresDeductions = Number(emp.autres_deductions) || 0;
  const joursTravailles = Number(emp.jours_travailles) || 26;
  const personnesACharge = Number(emp.personnes_charge) || 0;
  const representationActif = !!emp.representation;
  const mutuelleSalarie = Number(emp.mutuelle_salarie) || 0;
  const cimrActif = !!emp.cimr;
  const avantagesNature = Number(emp.avantages_nature) || 0;

  let brut, indDetail, detail;
  ({ brut, indDetail } = brutFromNet(net_cible, anciennete, emp.statut, autresDeductions, params, bareme, joursTravailles, personnesACharge, representationActif, mutuelleSalarie, cimrActif, avantagesNature));
  if (emp.statut === 'J') {
    // Journalier : la loi impose comme base de cotisation au moins le SMIG proratisé aux jours
    // travaillés (le brut réel), même si le net cible est vide/bas et ferait converger la dichotomie
    // vers un brut ~0. Au-delà de ce plancher, le brut résolu (SMIG + prime de rendement) reste
    // inchangé pour continuer à atteindre le net cible quand celui-ci le dépasse.
    brut = Math.max(brut, params.smig_brut * (Math.min(joursTravailles, 26) / 26));
  }
  detail = netFromBrut(brut, anciennete, indDetail.total, autresDeductions, params, bareme, personnesACharge, representationActif, mutuelleSalarie, cimrActif, avantagesNature);

  return {
    brut,
    brutDisplay: brut,
    cnss: detail.cnss,
    amo: detail.amo,
    fraisPro: detail.fraisPro,
    baseIR: detail.AX,
    ir: detail.ir,
    cimrMontant: detail.cimr,
    indTransport: indDetail.transport,
    indSalissure: indDetail.salissure,
    indOutillage: indDetail.outillage,
    indPanier: indDetail.panier,
    indCaisse: indDetail.caisse,
    indLait: indDetail.lait,
    indTournee: indDetail.tournee,
    indRepresentation: detail.representation,
    indemnitesTotal: indDetail.total + detail.representation,
    mutuelleSalarie,
    avantagesNature,
    chargesPatronales: detail.chargesPatronales,
    coutEmployeur: detail.coutEmployeur,
    primeRendement: (() => {
      const prorata = Math.min(joursTravailles, 26) / 26;
      const maxInd = INDEMNITE_ORDER.reduce((s, k) => {
        const c = params.indemnites[k];
        return c?.actif ? s + c.plafond * prorata : s;
      }, 0);
      // brut est comparé au SMIG proratisé (comme le plancher appliqué plus haut), sinon un
      // journalier à peu de jours (ex: 5j) se retrouve avec brut - SMIG plein mois = très négatif.
      return indDetail.total >= maxInd - 0.01 ? brut - params.smig_brut * prorata : 0;
    })(),
    netRecalcule: detail.netFinal,
    ecart: detail.netFinal - net_cible,
  };
}
